import type { ReactNode } from "react"
import { useRef, useState } from "react"

import { cx } from "../lib/cx"
import type { JournalWeek, WeekDay, WeekSlot } from "./journal-week"
import {
  slotText,
  slotTitle,
  slotTone,
  WEEK_DRAG_NOTE,
  WEEK_SLOT_MINUTES,
  weekHourLabels,
} from "./journal-week"
import styles from "./journal-week-grid.module.css"
import "./journal-tokens.css"

/**
 * 2-2.5 «Неделя» журнала — сетка семь дней × получасы.
 *
 * ДВА ПОДРЕЖИМА В ОДНОЙ СЕТКЕ, но рисуют они разное. «Загрузка салона» кладёт в клетку
 * число «3/6» и заливает её по занятости: администратор ищет глазами светлое, то есть
 * то, что можно продать. «Один мастер» кладёт в клетку карточки этого мастера: тот же
 * вопрос, но про конкретного человека.
 *
 * ВЫСОТА СТРОКИ ПОСТОЯННА — 24 px на получас, как в макете. В дне сетка считает
 * пиксели на минуту, потому что запись может начаться между линиями; здесь же клетка
 * это единица, и запись в ней либо есть, либо нет. Пунктирная рамка отмечает запись,
 * начавшуюся не на линии: иначе её край не отличить от соседней клетки.
 *
 * КЛЕТКА — НЕ КНОПКА, А ШАПКА ДНЯ — КНОПКА. Двадцать восемь клеток на день давали сто
 * шестьдесят восемь остановок табуляции: чтобы уйти с недели на клавиатуре, пришлось бы
 * нажать Tab сто шестьдесят восемь раз. Мышью клетка по-прежнему открывает день,
 * с клавиатуры — кнопка в шапке дня, одна цель на день.
 *
 * ПЕРЕТАСКИВАНИЯ НЕТ, И ЭТО СКАЗАНО СЛОВАМИ. Попытка тащить — первое, что человек
 * пробует в сетке; молчащий интерфейс он считает сломанным и пробует ещё трижды.
 */
export function JournalWeekGrid(props: {
  readonly week: JournalWeek
  /** Открыть день: клик по клетке — главное действие недели. */
  readonly onOpenDay: (date: string) => void
  /** Открыть карточку записи (режим «Один мастер»). */
  readonly onOpenCard?: ((appointmentId: string, date: string) => void) | undefined
}): ReactNode {
  const { week } = props
  const labels = weekHourLabels(week.openMinute, week.closeMinute)
  const rows = labels.length

  /*
   * ПОПЫТКА ПЕРЕТАЩИТЬ ЛОВИТСЯ ЖЕСТОМ МЫШИ, а не атрибутом draggable. Нативное
   * перетаскивание у div выключено по умолчанию, но человек всё равно жмёт и ведёт
   * мышь — и именно в этот момент ему нужно объяснение, а не тишина.
   *
   * ТОЛЬКО МЫШЬ И ТОЛЬКО С ЗАЖАТОЙ КНОПКОЙ. Палец, прокручивающий неделю, — это
   * прокрутка, а не попытка тащить: без этой проверки плашка вылезала на каждом
   * движении пальца, и объяснение превращалось в помеху.
   */
  const [dragNote, setDragNote] = useState(false)
  const pressAt = useRef<{ x: number; y: number } | null>(null)
  /*
   * Жест гасит СЛЕДУЮЩИЙ щелчок. Без этого объяснение исчезало в тот же миг: браузер
   * после проводки мышью присылает click, клик открывал день — и человек уходил
   * с недели, успев увидеть плашку на один кадр.
   */
  const dragged = useRef(false)

  const onPointerDown = (event: React.PointerEvent): void => {
    if (event.pointerType !== "mouse" || event.buttons === 0) return
    pressAt.current = { x: event.clientX, y: event.clientY }
  }
  const onPointerMove = (event: React.PointerEvent): void => {
    const from = pressAt.current
    if (from === null) return
    if (event.buttons === 0) {
      pressAt.current = null
      return
    }
    // Двенадцать пикселей — это уже не дрожь руки, а намерение тащить.
    if (Math.abs(event.clientX - from.x) > 12 || Math.abs(event.clientY - from.y) > 12) {
      pressAt.current = null
      dragged.current = true
      setDragNote(true)
    }
  }
  const onPointerUp = (): void => {
    pressAt.current = null
  }

  /** Открыть день, если это был щелчок, а не конец проводки. */
  const openDay = (date: string): void => {
    if (dragged.current) {
      dragged.current = false
      return
    }
    props.onOpenDay(date)
  }
  const openCard = (appointmentId: string, date: string): void => {
    if (dragged.current) {
      dragged.current = false
      return
    }
    props.onOpenCard?.(appointmentId, date)
  }

  return (
    <div className={styles["wrap"]}>
      {dragNote ? (
        <div className={styles["dragNote"]} role="status">
          <span>{WEEK_DRAG_NOTE}</span>
          <button
            type="button"
            className={styles["dragClose"]}
            onClick={() => {
              setDragNote(false)
            }}
          >
            Понятно
          </button>
        </div>
      ) : null}

      <div className={styles["scroll"]}>
        <div
          className={styles["grid"]}
          role="grid"
          aria-label={
            week.mode === "master"
              ? `Неделя мастера ${week.masterName ?? ""}`.trim()
              : "Загрузка салона по неделе"
          }
          style={{ gridTemplateRows: `auto repeat(${String(rows)}, var(--week-row))` }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          onPointerLeave={onPointerUp}
        >
          {/* Угловая клетка шкалы времени: пустая, но нужна для выравнивания. */}
          <div className={styles["corner"]} />

          {week.days.map((day) => (
            <div
              key={day.date}
              role="columnheader"
              className={cx(styles["head"], day.isToday && styles["headToday"])}
            >
              {/*
                * ДЕНЬ ОТКРЫВАЕТСЯ ОТСЮДА И С КЛАВИАТУРЫ. Это единственная остановка
                * табуляции на весь день недели.
                */}
              <button
                type="button"
                className={styles["headDay"]}
                onClick={() => {
                  openDay(day.date)
                }}
              >
                {day.label}
                <span className={styles["srOnly"]}> — открыть день</span>
              </button>
              <span className={styles["headSub"]}>{day.sub}</span>
            </div>
          ))}

          {labels.map((label, row) => (
            <div key={`t-${String(row)}`} className={styles["time"]} style={{ gridRow: row + 2 }}>
              {label}
            </div>
          ))}

          {week.days.map((day, column) =>
            week.mode === "load"
              ? day.slots.map((slot, row) => (
                  <LoadCell
                    key={`${day.date}-${String(slot.minute)}`}
                    slot={slot}
                    dayLabel={day.label}
                    row={row + 2}
                    column={column + 2}
                    onOpen={() => {
                      openDay(day.date)
                    }}
                  />
                ))
              : (
                  <MasterColumn
                    key={day.date}
                    day={day}
                    column={column + 2}
                    openMinute={week.openMinute}
                    rows={rows}
                    onOpenDay={() => {
                      openDay(day.date)
                    }}
                    onOpenCard={openCard}
                  />
                ),
          )}
        </div>
      </div>
    </div>
  )
}

/**
 * Клетка «Загрузки салона»: число занятых из работающих и заливка по ступени.
 *
 * ИМЯ КЛЕТКИ НАЗЫВАЕТ ДЕНЬ И ВРЕМЯ. «3/6» без дня и часа не значит ничего ни для чтеца,
 * ни для того, кто навёл мышь на середину сетки.
 */
function LoadCell(props: {
  readonly slot: WeekSlot
  readonly dayLabel: string
  readonly row: number
  readonly column: number
  readonly onOpen: () => void
}): ReactNode {
  const tone = slotTone(props.slot)
  const text = slotText(props.slot)
  const name = `${props.dayLabel}, ${slotTitle(props.slot)}`
  return (
    <div
      role="gridcell"
      className={cx(styles["cell"], styles[`tone-${tone}`])}
      style={{ gridRow: props.row, gridColumn: props.column }}
      title={name}
      aria-label={name}
      // Клетка «не работает» тоже открывает день: посмотреть, почему смен нет, —
      // законное желание, и запирать его незачем.
      onClick={props.onOpen}
    >
      {text}
    </div>
  )
}

/**
 * Колонка дня в режиме «Один мастер».
 *
 * Карточки лежат абсолютно внутри одной клетки на всю высоту дня: запись может
 * начаться в 16:20 и длиться 40 минут, и раскладывать её по получасовым клеткам
 * значило бы соврать о времени — ровно то, чего сетка дня не делает.
 *
 * КАРТОЧКИ РИСУЮТСЯ И БЕЗ СМЕНЫ. Запись вне смены — штатное состояние продукта
 * (администратор подтверждает предупреждение «у мастера нет смены», и запись
 * создаётся), а раньше такой день выходил пустой штриховкой: клиент есть, а на экране
 * его нет. Штриховка осталась фоном — она говорит «смены нет», а не «никого нет».
 */
function MasterColumn(props: {
  readonly day: WeekDay
  readonly column: number
  readonly openMinute: number
  readonly rows: number
  readonly onOpenDay: () => void
  readonly onOpenCard: (appointmentId: string, date: string) => void
}): ReactNode {
  const { day } = props
  const perMinute = 1 / WEEK_SLOT_MINUTES
  const top = (minute: number): string =>
    `calc((${String((minute - props.openMinute) * perMinute)}) * var(--week-row))`
  const height = (from: number, to: number): string =>
    `calc((${String((to - from) * perMinute)}) * var(--week-row) - 2px)`

  return (
    <div
      role="gridcell"
      className={styles["column"]}
      style={{ gridRow: `2 / span ${String(props.rows)}`, gridColumn: props.column }}
    >
      {/* Нерабочее время — штриховкой: пустая клетка и «не работает» это разное. */}
      <div
        className={day.shift === null ? styles["offDay"] : styles["dayCanvas"]}
        onClick={props.onOpenDay}
        title={day.shift === null ? `${day.label}: смены нет` : `${day.label}: ${day.sub}`}
        aria-hidden="true"
      />
      {day.cards.map((card) => (
        <button
          key={card.id}
          type="button"
          className={cx(
            card.status === "cancelled" ? styles["cardCancelled"] : styles["card"],
            card.offGrid && styles["cardOffGrid"],
          )}
          style={{ top: top(card.startMinute), height: height(card.startMinute, card.endMinute) }}
          title={`${card.clientName} · ${card.timeLabel}`}
          onClick={(event) => {
            event.stopPropagation()
            props.onOpenCard(card.id, day.date)
          }}
        >
          <b className={styles["cardName"]}>{card.clientName}</b> {card.timeLabel}
        </button>
      ))}
    </div>
  )
}
